import moment from "moment";
import type { stringOrDate } from "react-big-calendar";

/**
 * Compute the new dueDate (ISO string, UTC, truncated to the minute) for a
 * calendar drag-drop of a scheduled message.
 *
 * FE#264: users deliberately schedule at non-round times (e.g. 12:12). When an
 * event is dragged to ANOTHER DAY (the common day-to-day drag), react-big-calendar's
 * drop `start` snaps to the day-cell slot (a round hour / start of day), discarding
 * the user's chosen time. On a CROSS-DAY move we therefore keep the source
 * time-of-day (hour/minute/second) and only change the date component.
 *
 * On an INTRA-DAY move (dropped on the SAME calendar day, e.g. onto a specific
 * time slot in week/day view) the drop `start` IS the intended new time, so we
 * use it as-is — preserving the existing behaviour of retiming within a day.
 *
 * Cross-day is judged in the same (local) frame the calendar renders the event in,
 * so the comparison matches what the user sees and dragged.
 */
export function computeDroppedDueDate(
    sourceDueDate: stringOrDate,
    start: stringOrDate,
    /**
     * Does `start` carry a time the user actually chose?
     *
     * In week and day view it does — the drop lands on a time slot, and an
     * intra-day move is how you retime a message. A MONTH cell does not: the
     * grid resolves a month drop to the day's midnight because that is all a
     * day cell knows. Trusting that midnight as an intended time re-timed a
     * 10:00 message to 00:00 whenever it was dropped back on its own day, and
     * since midnight differs from the source the move also stopped registering
     * as a no-op — so a recurring occurrence was split out of its series for a
     * move the user never made. Callers with no time to offer pass `false`.
     */
    targetCarriesTimeOfDay = true
): string {
    const source = moment(sourceDueDate);
    const target = moment(start);

    if (!targetCarriesTimeOfDay || !source.isSame(target, "day")) {
        // Cross-day move: carry the source time-of-day onto the target date.
        target.set({
            hour: source.hour(),
            minute: source.minute(),
            second: source.second(),
        });
    }

    return target.startOf("minute").toISOString();
}
